// src/pages/payout.ts —— 06 C-16 代付管理（代付订单 / 收款账户 / 代付额度）与 C-17 收单订单与物流（订单 / 退款 / 物流跟踪 / 支付资产）页定义。
// PAGES_PAYOUT 经 pages/index 注册，后序覆盖 PAGES_SHELLS 同名搭壳页。C-18 上游页为 dash 原生页（domains/dash.upstream.ts）。
// 每个 await 之后的状态写入先过会话守卫；写动作在途锁为「宿主 × 动作键」（表单 / 确认框由 table 机制 busy 兜底）
import { ApiError } from '../../../shared/src/api/client';
import type { RouteKey } from '../../../shared/src/api/routes';
import type { Dict } from '../../../shared/src/dc';
import { interpolate } from '../../../shared/src/errors';
import { fmtAmount, parseDecimalInput, toMicro } from '../../../shared/src/money';
import { fmtDateTime } from '../../../shared/src/time';
import { closeDrawer, openConfirm, openDrawer, openForm, reloadCurrent, sessionGuard } from '../domains/table';
import { str, strArr, type AdminCtx } from '../domains/types';
import { I18N_PAYOUT, REWRITES_PAYOUT } from '../i18n/payout';
import type { AssetRow, BeneficiaryRow, LogisticsRow, PaymentOrderRow, PayoutPage, PayoutQuota, PayoutRow, QuotaRow, TimelineNode } from './payout.types';
import { chipMap, defineView, type ActionDef, type DrawerAction, type DrawerSpec, type ErasedView, type FormDef, type FormVals, type Need, type PageMap, type StatVals } from './types';
import { cell, chip, chipOf, localErr, type CellVals, type ChipKind } from './ui';

const PAY_EDIT: Need = { module: 'payout', bit: 'edit' };
const PAY_APPROVE: Need = { module: 'payout', bit: 'approve' };
const ORD_EDIT: Need = { module: 'orders', bit: 'edit' };
const EMPTY = '—';
const REMARK_MAX = 255;
const BEN_NAME_MAX = 128; // backend payout/beneficiary.go maxBenName
const TRACK_MAX = 64; // backend payment/ship.go carrierNameMaxLen / tracking_number
// 06 C-16 发起代付收款账户选项：size 白名单上限 50（02 §2.2），超出部分提示到收款账户页筛选（计划 §6-P9）
export const BEN_OPTION_SIZE = 50;

const tz = (ctx: AdminCtx): string | undefined => ctx.s.sh.timezone || undefined;
const chars = (s: string): number => [...s].length;

// D8-12：两位截断展示；后端值非法时原样显示（不抛错）
function amt(v: string): string {
  try {
    return fmtAmount(v, 2);
  } catch {
    return v;
  }
}
const usd = (v: string): string => `$${amt(v)}`;

function micro(v: string): bigint | null {
  try {
    return toMicro(v);
  } catch {
    return null;
  }
}

const textVal = (v: FormVals, k: string): string => {
  const x = v[k];
  return typeof x === 'string' ? x.trim() : '';
};

const chipText = (t: Dict, map: Readonly<Record<string, readonly [ChipKind, string]>>, v: string): string => {
  const hit = map[v];
  return hit ? str(t, hit[1]) || v : v;
};

// ---- 在途锁（宿主 × 动作键）：同一动作在途时重复触发直接忽略；结束（成功或失败）即释放 ----

const inflight = new WeakMap<object, Set<string>>();

// 异常原样抛给调用方（确认框 doConfirm / 主操作 runSafe 统一 toast）
export async function withLock(ctx: AdminCtx, key: string, fn: () => Promise<void>): Promise<void> {
  let keys = inflight.get(ctx.host);
  if (!keys) {
    keys = new Set();
    inflight.set(ctx.host, keys);
  }
  if (keys.has(key)) return;
  const held = keys;
  held.add(key);
  try {
    await fn();
  } finally {
    held.delete(key);
  }
}

// 业务码 → 本页文案（toast）并刷新列表；其余错误原样抛出（TD-846）
async function mapState(ctx: AdminCtx, e: unknown, codes: readonly number[], key: string): Promise<void> {
  if (!(e instanceof ApiError) || !codes.includes(e.code)) throw e;
  const alive = sessionGuard(ctx);
  ctx.gp.ui.toast(str(ctx.t, key));
  await reloadCurrent(ctx);
  if (alive() && ctx.host.state.tb.drawer) closeDrawer(ctx);
}

// ==================== C-16 代付管理 ====================

// 06 C-16 状态列
const PAYOUT_STATUS: Readonly<Record<string, readonly [ChipKind, string]>> = {
  pending: ['gray', 'stSubmitted'], processing: ['amber', 'stProcessing'], success: ['green', 'stSuccess'], failed: ['red', 'stFailed'],
};
// 06 C-16 对账列（NULL → —）
const RECONCILE: Readonly<Record<string, readonly [ChipKind, string]>> = { scheduled: ['blue', 'stRecScheduled'], manual_review: ['amber', 'stRecManual'] };

function isQuota(v: unknown): v is PayoutQuota {
  if (typeof v !== 'object' || v === null) return false;
  return 'limit' in v && typeof v.limit === 'string' && 'used' in v && typeof v.used === 'string' && 'left' in v && typeof v.left === 'string' && 'stale' in v && typeof v.stale === 'boolean';
}

export function quotaOf(page: Readonly<Record<string, unknown>>): PayoutQuota | null {
  const q = page['quota'];
  return isQuota(q) ? q : null;
}

// 06 C-16 统计卡（三页签共用）：合计 = quota.limit（暗底，sub 代付额度；stale 时 sub 换同步失败提示）/ 冻结 = used（sub 同步于 HH:mm）/ 可用 = left；
// quota 缺失或 null → 「—」（D8-12 截断展示，不以当前页数据聚合冒充）。06 未给阈值，不标红
export function quotaStats(ctx: AdminCtx, page: Readonly<Record<string, unknown>>): readonly StatVals[] {
  const t = ctx.t;
  const q = quotaOf(page);
  const synced = q ? fmtDateTime(q.synced_at, { tz: tz(ctx) }) : EMPTY;
  return [
    { label: str(t, 'quotaLimit'), value: q ? usd(q.limit) : EMPTY, sub: str(t, q?.stale ? 'payQuotaStale' : 'payoutQuota'), dark: true },
    { label: str(t, 'quotaUsed'), value: q ? usd(q.used) : EMPTY, sub: q && synced !== EMPTY ? `${str(t, 'syncedAt')} ${synced.slice(11)}` : EMPTY },
    { label: str(t, 'quotaLeft'), value: q ? usd(q.left) : EMPTY, sub: '' },
  ];
}

const benText = (r: PayoutRow): string => r.beneficiary_name ?? r.beneficiary_no;

// ---- 发起代付（formDef payout）----

// 金额：>0、≤6 位小数；规范化为输入原样十进制串（服务端 money.Parse）
function normPayAmount(x: string | boolean | undefined): string | null {
  const s = typeof x === 'string' ? x.trim() : '';
  const n = parseDecimalInput(s, 6);
  if (n === null) return null;
  const m = micro(n);
  return m !== null && m > 0n ? n : null;
}

export function payoutForm(ctx: AdminCtx, bens: readonly BeneficiaryRow[], total: number, quota: PayoutQuota | null): FormDef {
  const t = ctx.t;
  const route: RouteKey = 'POST /api/admin/payouts';
  const labels = new Map<string, string>(bens.map((b) => [b.beneficiary_no, `${b.name} · ${b.payout_method} · ${b.account_masked}`]));
  // 模板 <option> 无 disabled 槽位：提示项取空值，选中即触发必填红条，不可提交（计划 §6-P9）
  const options = [...bens.map((b) => b.beneficiary_no), ...(total > BEN_OPTION_SIZE ? [''] : [])];
  const left = quota ? micro(quota.left) : null;
  return {
    title: str(t, 'newPayout'),
    fields: [
      { k: 'beneficiary_no', label: str(t, 'cAcct'), type: 'select', options, optionLabel: (d, v) => (v === '' ? str(d, 'payBenMore') : labels.get(v) ?? v) },
      { k: 'amount', label: `${str(t, 'cAmount')} (USD)`, type: 'number' },
      { k: 'currency', label: str(t, 'cCurrency'), type: 'select', options: ['USD'] },
      { k: 'remark', label: str(t, 'cRemark'), type: 'text', wide: true, optional: true },
    ],
    init: { beneficiary_no: bens[0]?.beneficiary_no ?? '', amount: '100', currency: 'USD', remark: '' },
    validate: (v) => {
      const a = normPayAmount(v['amount']);
      if (a === null) return str(t, 'payErrAmount');
      const m = micro(a);
      if (quota && left !== null && m !== null && m > left) return interpolate(str(t, 'payErrOverQuota'), { left: usd(quota.left) });
      if (chars(textVal(v, 'remark')) > REMARK_MAX) return str(t, 'payErrRemark');
      return '';
    },
    submit: async (c, v) => {
      const amount = normPayAmount(v['amount']);
      if (amount === null) throw localErr(route, str(t, 'payErrAmount'));
      const alive = sessionGuard(c);
      await c.gp.api.call(route, { body: { beneficiary_no: textVal(v, 'beneficiary_no'), amount, currency: 'USD', remark: textVal(v, 'remark') } });
      if (!alive()) return;
      c.gp.ui.toast(str(t, 'toastPayout'));
      await reloadCurrent(c);
    },
  };
}

// 收款账户选项在表单打开时拉取（仅启用账户，首 50 条）；同一宿主在途时重复点击忽略，会话已换则不打开
export async function openPayoutForm(ctx: AdminCtx): Promise<void> {
  await withLock(ctx, 'payout:new', async () => {
    const alive = sessionGuard(ctx);
    const r = await ctx.gp.api.call<PayoutPage<BeneficiaryRow>>('GET /api/admin/payout-beneficiaries', { query: { status: 1, page: 1, size: BEN_OPTION_SIZE } });
    if (!alive()) return;
    const list = Array.isArray(r.list) ? r.list : [];
    openForm(ctx, payoutForm(ctx, list, typeof r.total === 'number' ? r.total : list.length, isQuota(r.quota) ? r.quota : null));
  });
}

// ---- 重试 / 人工确认终态 ----

async function retryPayout(ctx: AdminCtx, row: PayoutRow): Promise<void> {
  await withLock(ctx, `payout:retry:${row.id}`, async () => {
    const alive = sessionGuard(ctx);
    try {
      await ctx.gp.api.call('POST /api/admin/payouts/{id}/retry', { path: { id: row.id }, body: {} });
    } catch (e) {
      if (!alive()) return;
      return mapState(ctx, e, [1012], 'payErrRetryState');
    }
    if (!alive()) return;
    ctx.gp.ui.toast(str(ctx.t, 'toastPayout'));
    if (ctx.host.state.tb.drawer) closeDrawer(ctx);
    await reloadCurrent(ctx);
  });
}

// 重试 = 新建上游单（06 C-16 行内动作 / 审计 C-17）：先确认（payRetryTitle / payRetryText）
export function confirmRetry(ctx: AdminCtx, row: PayoutRow): void {
  const t = ctx.t;
  openConfirm(ctx, { title: str(t, 'payRetryTitle'), text: str(t, 'payRetryText'), label: row.biz_no, run: (c) => retryPayout(c, row) });
}

const outcomeLabel = (t: Dict, v: string): string => (v === 'success' ? str(t, 'stSuccess') || v : v === 'failed' ? str(t, 'stFailed') || v : v);

export function reconcileForm(ctx: AdminCtx, row: PayoutRow): FormDef {
  const t = ctx.t;
  return {
    title: interpolate(str(t, 'recTitle'), { biz_no: row.biz_no }),
    tip: str(t, 'recTip'),
    fields: [
      { k: 'outcome', label: str(t, 'recOutcome'), type: 'select', options: ['success', 'failed'], optionLabel: outcomeLabel },
      // 必填由 validate 给出专用文案（通用必填红条不含长度约束）
      { k: 'note', label: str(t, 'recNote'), type: 'text', wide: true, optional: true, placeholder: str(t, 'payRecNotePh') },
    ],
    init: { outcome: 'success', note: '' },
    validate: (v) => {
      const note = textVal(v, 'note');
      return note === '' || chars(note) > REMARK_MAX ? str(t, 'payErrRecNote') : '';
    },
    submit: async (c, v) => {
      const alive = sessionGuard(c);
      await c.gp.api.call('POST /api/admin/payouts/{id}/reconcile', { path: { id: row.id }, body: { outcome: textVal(v, 'outcome'), note: textVal(v, 'note') } });
      if (!alive()) return;
      c.gp.ui.toast(str(t, 'toastPayoutReconciled'));
      if (c.host.state.tb.drawer) closeDrawer(c);
      await reloadCurrent(c);
    },
  };
}

const failed = (r: PayoutRow): boolean => r.payout_status === 'failed';
const manual = (r: PayoutRow): boolean => r.reconcile_status === 'manual_review';

function payoutRowActions(): readonly ActionDef<PayoutRow>[] {
  return [
    { label: 'aDetail', run: (c, r) => openPayoutDrawer(c, r) },
    { label: 'aRetry', need: PAY_EDIT, show: failed, run: (c, r) => confirmRetry(c, r) },
    { label: 'payAReconcile', need: PAY_APPROVE, show: manual, run: (c, r) => openForm(c, reconcileForm(c, r)) },
  ];
}

// 06 C-16 抽屉：字段格 8 项；JSON 区（最近回调 payload）无后端单笔详情出口，不渲染（计划 §6-P4）
export function payoutDrawer(ctx: AdminCtx, r: PayoutRow): DrawerSpec {
  const t = ctx.t;
  const z = tz(ctx);
  const actions: DrawerAction[] = [];
  if (failed(r)) actions.push({ label: 'aRetry', kind: 'green', need: PAY_EDIT, run: (c) => confirmRetry(c, r) });
  if (manual(r)) actions.push({ label: 'payAReconcile', kind: 'green', need: PAY_APPROVE, run: (c) => openForm(c, reconcileForm(c, r)) });
  if (actions.length === 0) actions.push({ label: 'aClose', kind: 'primary', run: (c) => closeDrawer(c) });
  return {
    kind: str(t, 'payout'),
    title: r.biz_no,
    subtitle: benText(r),
    fields: [
      { k: str(t, 'cRemark'), v: r.remark || EMPTY, wide: true },
      { k: str(t, 'cAmount'), v: `${amt(r.amount)} ${r.currency}` },
      { k: str(t, 'payFeeUpstream'), v: r.fee === null ? EMPTY : usd(r.fee) },
      { k: str(t, 'cStatus'), v: chipText(t, PAYOUT_STATUS, r.payout_status) },
      { k: str(t, 'cReconcile'), v: r.reconcile_status === null ? EMPTY : chipText(t, RECONCILE, r.reconcile_status) },
      { k: 'system_order_number', v: r.system_order_number ?? EMPTY },
      { k: 'merchant_order_number', v: r.merchant_order_number },
      { k: str(t, 'cTime'), v: fmtDateTime(r.created_at, { tz: z }) },
    ],
    actions,
  };
}

export function openPayoutDrawer(ctx: AdminCtx, r: PayoutRow): void {
  openDrawer(ctx, payoutDrawer(ctx, r));
}

function payoutCells(ctx: AdminCtx, r: PayoutRow): readonly CellVals[] {
  const t = ctx.t;
  return [
    cell(r.biz_no, r.system_order_number ?? EMPTY, true),
    cell(benText(r)),
    cell(r.remark || EMPTY),
    cell(`${amt(r.amount)} ${r.currency}`),
    cell(r.fee === null ? EMPTY : usd(r.fee)),
    chipOf(chipMap(t, PAYOUT_STATUS), r.payout_status, 'payout.payout_status'),
    r.reconcile_status === null ? cell(EMPTY) : chipOf(chipMap(t, RECONCILE), r.reconcile_status, 'payout.reconcile_status'),
    cell(fmtDateTime(r.created_at, { tz: tz(ctx) })),
  ];
}

function payoutOrdersView(): ErasedView {
  return defineView<PayoutRow>({
    key: 'orders',
    source: { route: 'GET /api/admin/payouts', tabParam: 'none' },
    echo: '/admin/payouts',
    cols: [
      { label: 'cPayoutId' }, { label: 'cAcct' }, { label: 'cRemark' }, { label: 'cAmount', facet: 'currency' }, { label: 'cFee' },
      { label: 'cStatus', facet: 'status', facetLabel: (t, v) => chipText(t, PAYOUT_STATUS, v) }, { label: 'cReconcile' }, { label: 'cTime', sortKey: 'created_at' },
    ],
    cells: payoutCells,
    open: openPayoutDrawer,
    rowActions: () => payoutRowActions(),
    stats: quotaStats,
    primary: { label: 'newPayout', icon: '+ ', need: PAY_EDIT, run: openPayoutForm },
    csvName: 'payouts',
  });
}

// ---- 收款账户（formDef payacct）----

const BEN_STATUS_FACET: Readonly<Record<string, string>> = { '1': 'stActive', '0': 'stPending' };
const benStatusChip = (t: Dict, on: boolean): CellVals => (on ? chip('green', str(t, 'stActive')) : chip('amber', str(t, 'stPending')));
const METHOD_CHIP: Readonly<Record<string, ChipKind>> = { paypal: 'blue', cashapp: 'green' };
const methodChip = (m: string): CellVals => chipOf(Object.fromEntries(Object.entries(METHOD_CHIP).map(([k, kind]) => [k, [kind, k] as const])), m, 'beneficiary.payout_method');
const COUNTRY_RE = /^[A-Z]{2}$/;
const statusLabel = (t: Dict, v: string): string => str(t, v === '1' ? 'payStEnable' : v === '0' ? 'payStDisable' : '') || v;

// 新增全字段；编辑时类型 / payout_method / 币种只读（后端拒绝变更，payout/beneficiary.go UpdateBeneficiary），账号须重填（列表只出掩码）
export function benForm(ctx: AdminCtx, row: BeneficiaryRow | null): FormDef {
  const t = ctx.t;
  const edit = row !== null;
  const ro = edit ? ('readonly' as const) : ('select' as const);
  return {
    title: `${str(t, edit ? 'aEdit' : 'newAcct')}${edit ? ` · ${row.name}` : ''}`,
    fields: [
      { k: 'name', label: str(t, 'cAcct'), type: 'text' },
      { k: 'type', label: str(t, 'cAcctType'), type: ro, options: ['individual', 'enterprise'] },
      { k: 'payout_method', label: 'payout_method', type: ro, options: ['paypal', 'cashapp'] },
      { k: 'account', label: 'Account / address', type: 'text', wide: true, placeholder: row?.account_masked ?? '' },
      { k: 'currency', label: str(t, 'cCurrency'), type: ro, options: ['USD'] },
      { k: 'country', label: str(t, 'cCountry'), type: 'text', optional: true },
      { k: 'status', label: str(t, 'cStatus'), type: 'select', options: ['1', '0'], optionLabel: statusLabel },
    ],
    init: {
      name: row?.name ?? '', type: row?.type ?? 'individual', payout_method: row?.payout_method ?? 'paypal', account: '', currency: row?.currency ?? 'USD',
      country: row?.country ?? '', status: row === null || row.status ? '1' : '0',
    },
    validate: (v) => {
      const name = textVal(v, 'name');
      if (name === '' || chars(name) > BEN_NAME_MAX) return str(t, 'payErrBenName');
      const country = textVal(v, 'country');
      if (country !== '' && !COUNTRY_RE.test(country)) return str(t, 'payErrCountry');
      return '';
    },
    submit: async (c, v) => {
      const country = textVal(v, 'country');
      const common = { name: textVal(v, 'name'), account: textVal(v, 'account'), ...(country ? { country } : {}), status: v['status'] === '1' };
      const alive = sessionGuard(c);
      if (row) await c.gp.api.call('PUT /api/admin/payout-beneficiaries/{id}', { path: { id: row.id }, body: common });
      else await c.gp.api.call('POST /api/admin/payout-beneficiaries', { body: { ...common, type: textVal(v, 'type'), payout_method: textVal(v, 'payout_method'), currency: textVal(v, 'currency') } });
      if (!alive()) return;
      c.gp.ui.toast(str(t, 'toastSaved'));
      if (c.host.state.tb.drawer) closeDrawer(c);
      await reloadCurrent(c);
    },
  };
}

// 删除透传上游（06 C-16 Tab1）：确认后在途锁 `ben:del:{id}`，在途期间再次确认不重复 DELETE
export function confirmDeleteBen(ctx: AdminCtx, row: BeneficiaryRow): void {
  const t = ctx.t;
  openConfirm(ctx, {
    title: str(t, 'delTitle'),
    text: str(t, 'delText'),
    label: row.name,
    run: (c) =>
      withLock(c, `ben:del:${row.id}`, async () => {
        const alive = sessionGuard(c);
        await c.gp.api.call('DELETE /api/admin/payout-beneficiaries/{id}', { path: { id: row.id } });
        if (!alive()) return;
        c.gp.ui.toast(str(t, 'toastDeleted'));
        await reloadCurrent(c);
      }),
  });
}

export function benDrawer(ctx: AdminCtx, r: BeneficiaryRow): DrawerSpec {
  const t = ctx.t;
  return {
    kind: str(t, 'payout'),
    title: r.name,
    subtitle: r.account_masked,
    fields: [
      { k: 'ID', v: r.beneficiary_no },
      { k: str(t, 'cAcctType'), v: `${r.payout_method} · ${r.type}` },
      { k: str(t, 'cCurrency'), v: r.currency },
      { k: str(t, 'cStatus'), v: str(t, r.status ? 'stActive' : 'stPending') },
    ],
    actions: [{ label: 'aEdit', kind: 'primary', need: PAY_EDIT, run: (c) => openForm(c, benForm(c, r)) }],
  };
}

function accountsView(): ErasedView {
  return defineView<BeneficiaryRow>({
    key: 'accounts',
    source: { route: 'GET /api/admin/payout-beneficiaries', tabParam: 'none' },
    echo: '/admin/payout-beneficiaries',
    cols: [
      { label: 'cAcct' }, { label: 'cAcctType', facet: 'account_type' }, { label: 'cCurrency', facet: 'currency' }, { label: 'cCountry' },
      { label: 'cCreated', sortKey: 'created_at' }, { label: 'cStatus', facet: 'status', facetLabel: (t, v) => str(t, BEN_STATUS_FACET[v] ?? '') || v },
    ],
    cells: (c, r) => [cell(r.name, r.account_masked), methodChip(r.payout_method), cell(r.currency), cell(r.country ?? EMPTY), cell(fmtDateTime(r.created_at, { tz: tz(c) })), benStatusChip(c.t, r.status)],
    open: (c, r) => openDrawer(c, benDrawer(c, r)),
    rowActions: () => [
      { label: 'aEdit', need: PAY_EDIT, run: (c, r) => openForm(c, benForm(c, r)) },
      { label: 'aDelete', need: PAY_EDIT, danger: true, run: (c, r) => confirmDeleteBen(c, r) },
    ],
    stats: quotaStats,
    primary: { label: 'newAcct', icon: '+ ', need: PAY_EDIT, run: (c) => openForm(c, benForm(c, null)) },
    csvName: 'payout-accounts',
  });
}

// ---- 代付额度（只读，B1 列表协议）----

function quotaStatus(t: Dict, r: QuotaRow): CellVals {
  const base = r.status === null ? cell(EMPTY) : r.status ? chip('green', str(t, 'stActive')) : chip('gray', str(t, 'stOff'));
  return r.stale ? { ...base, sub: str(t, 'payQuotaStale') } : base;
}

export function quotaDrawer(ctx: AdminCtx, r: QuotaRow): DrawerSpec {
  const t = ctx.t;
  return {
    kind: str(t, 'payoutQuota'),
    title: `${r.currency} · ${str(t, 'quotaLimit')}`,
    subtitle: usd(r.limit),
    fields: [
      { k: str(t, 'cCurrency'), v: r.currency },
      { k: str(t, 'quotaLimit'), v: usd(r.limit) },
      { k: str(t, 'quotaUsed'), v: usd(r.used) },
      { k: str(t, 'quotaLeft'), v: usd(r.left) },
      { k: str(t, 'payCMethod'), v: r.method ?? EMPTY },
      { k: str(t, 'cStatus'), v: `${r.status === null ? EMPTY : str(t, r.status ? 'stActive' : 'stOff')}${r.stale ? ` · ${str(t, 'payQuotaStale')}` : ''}` },
      { k: str(t, 'cUpdated'), v: fmtDateTime(r.updated_at, { tz: tz(ctx) }), wide: true },
    ],
    actions: [{ label: 'aClose', kind: 'primary', run: (c) => closeDrawer(c) }],
  };
}

function quotaView(): ErasedView {
  return defineView<QuotaRow>({
    key: 'quota',
    source: { route: 'GET /api/admin/payout-quota', tabParam: 'none' },
    echo: '/admin/payout-quota',
    cols: [
      { label: 'cCurrency' }, { label: 'quotaLimit' }, { label: 'quotaUsed' }, { label: 'quotaLeft' }, { label: 'payCMethod' },
      { label: 'cUpdated', sortKey: 'updated_at' }, { label: 'cStatus' },
    ],
    cells: (c, r) => [cell(r.currency), cell(usd(r.limit)), cell(usd(r.used)), cell(usd(r.left)), cell(r.method ?? EMPTY), cell(fmtDateTime(r.updated_at, { tz: tz(c) })), quotaStatus(c.t, r)],
    open: (c, r) => openDrawer(c, quotaDrawer(c, r)),
    rowActions: () => [{ label: 'aDetail', run: (c, r) => openDrawer(c, quotaDrawer(c, r)) }],
    stats: quotaStats,
    csvName: 'payout-quota',
    noDate: true, // spec §4.8 B1：参数白名单 sync / page / size / sort / order
    noKeyword: true,
  });
}

// ==================== C-17 收单订单与物流 ====================

const PAID = new Set(['processing', 'completed', 'processed']);
const ORDER_RED = new Set(['refund_failed', 'failed', 'abnormal', 'chargeback', 'disputed', 'dispute_processing', 'dispute_refund']);
const ORDER_GRAY = new Set(['pending', 'cancelled', 'dispute_resolved']);

export const refundable = (r: PaymentOrderRow): boolean => r.order_status !== null && PAID.has(r.order_status);
// 「已支付」态且无有效运单（有效运单 = shipment_status transit，02 §6.2 本地派生）
export const shippable = (r: PaymentOrderRow): boolean => refundable(r) && r.shipment_status !== 'transit';

// 06 C-17 Tab0 状态章：上游 order_status 值域映射，未映射值 gray + 原值（X-34）
export function orderChip(t: Dict, r: PaymentOrderRow): CellVals {
  const s = r.order_status;
  if (s === null) return cell(EMPTY);
  if (PAID.has(s)) return r.shipment_status === 'transit' ? chip('blue', str(t, 'stShipped')) : chip('green', str(t, 'stPaid'));
  if (s === 'refund_processing') return chip('amber', str(t, 'stRefunding'));
  if (s === 'refunded') return chip('gray', str(t, 'stRefund'));
  if (s === 'partially_refunded') return { ...chip('gray', str(t, 'stRefund')), sub: s };
  if (ORDER_RED.has(s)) return chip('red', s);
  if (ORDER_GRAY.has(s)) return chip('gray', s);
  return chipOf({}, s, 'order.order_status');
}

// 06 C-17 Tab2 物流状态（本地派生）：transit 运输中 blue / pending 待发货 amber；原型 delivered 删除
const SHIP_STATUS: Readonly<Record<string, readonly [ChipKind, string]>> = { transit: ['blue', 'stTransit'], pending: ['amber', 'stAwaitShip'] };

// 上游承运商码 → 06 表单文案（其余码原样）
const CARRIERS = ['DHL', 'SF_EX', 'UPS', 'FEDEX', 'OTHER'] as const;
const CARRIER_LABEL: Readonly<Record<string, string>> = { DHL: 'DHL Express', SF_EX: 'SF Express' };
const carrierText = (carrier: string | null, other: string | null): string => (carrier === null ? EMPTY : carrier === 'OTHER' ? other || carrier : CARRIER_LABEL[carrier] ?? carrier);

export function shipForm(ctx: AdminCtx, orderId: number, orderNo: string): FormDef {
  const t = ctx.t;
  const route: RouteKey = 'POST /api/admin/orders/{id}/ship';
  return {
    title: `${str(t, 'shipTitle')} · ${orderNo}`,
    fields: [
      { k: 'carrier', label: str(t, 'cCarrier'), type: 'select', options: CARRIERS, optionLabel: (_d, v) => CARRIER_LABEL[v] ?? v },
      // 模板无按值显隐：恒显示，承运商 = OTHER 时由 validate 要求必填（06 C-17「仅 OTHER 时显示」W-8 等价落地）
      { k: 'carrier_name_other', label: str(t, 'cCarrierOther'), type: 'text', optional: true },
      { k: 'tracking_number', label: str(t, 'cTracking'), type: 'text', wide: true, optional: true },
    ],
    init: { carrier: 'DHL', carrier_name_other: '', tracking_number: '' },
    validate: (v) => {
      const tn = textVal(v, 'tracking_number');
      if (tn === '' || chars(tn) > TRACK_MAX) return str(t, 'payErrTracking');
      const other = textVal(v, 'carrier_name_other');
      if (v['carrier'] === 'OTHER' && (other === '' || chars(other) > TRACK_MAX)) return str(t, 'payErrCarrierOther');
      return '';
    },
    submit: async (c, v) => {
      const carrier = textVal(v, 'carrier');
      const body = { carrier, tracking_number: textVal(v, 'tracking_number'), ...(carrier === 'OTHER' ? { carrier_name_other: textVal(v, 'carrier_name_other') } : {}) };
      const alive = sessionGuard(c);
      try {
        await c.gp.api.call(route, { path: { id: orderId }, body });
      } catch (e) {
        // 1012：已有在途运单 / 订单未同步（payment/ship.go）→ 刷新列表并在表单红条给出本页文案
        if (e instanceof ApiError && e.code === 1012) {
          if (alive()) await reloadCurrent(c);
          throw localErr(route, str(t, 'payErrShipState'));
        }
        throw e;
      }
      if (!alive()) return;
      c.gp.ui.toast(str(t, 'toastShip'));
      if (c.host.state.tb.drawer) closeDrawer(c);
      await reloadCurrent(c);
    },
  };
}

async function refundOrder(ctx: AdminCtx, row: PaymentOrderRow): Promise<void> {
  await withLock(ctx, `order:refund:${row.id}`, async () => {
    const alive = sessionGuard(ctx);
    try {
      await ctx.gp.api.call('POST /api/admin/orders/{id}/refund', { path: { id: row.id }, body: {} });
    } catch (e) {
      if (!alive()) return;
      // 4090 / HTTP 409：订单已不可退（payment/refund.go，R-2）
      return mapState(ctx, e, [4090], 'payErrRefundState');
    }
    if (!alive()) return;
    ctx.gp.ui.toast(str(ctx.t, 'toastRefund'));
    if (ctx.host.state.tb.drawer) closeDrawer(ctx);
    await reloadCurrent(ctx);
  });
}

export function confirmRefund(ctx: AdminCtx, row: PaymentOrderRow): void {
  const t = ctx.t;
  openConfirm(ctx, { title: str(t, 'refundTitle'), text: str(t, 'refundText'), label: row.merchant_order_number, run: (c) => refundOrder(c, row) });
}

function orderStatusText(t: Dict, r: PaymentOrderRow): string {
  const c = orderChip(t, r);
  return `${c.v}${r.payment_status ? ` · ${r.payment_status}` : ''}`;
}

export function orderDrawer(ctx: AdminCtx, r: PaymentOrderRow): DrawerSpec {
  const t = ctx.t;
  const actions: DrawerAction[] = [];
  if (refundable(r)) actions.push({ label: 'aRefund', kind: 'danger', need: ORD_EDIT, run: (c) => confirmRefund(c, r) });
  if (shippable(r)) actions.push({ label: 'aShip', kind: 'primary', need: ORD_EDIT, run: (c) => openForm(c, shipForm(c, r.id, r.merchant_order_number)) });
  if (actions.length === 0) actions.push({ label: 'aClose', kind: 'primary', run: (c) => closeDrawer(c) });
  const logi = strArr(t, 'tabOrders')[2] ?? '';
  return {
    kind: str(t, 'orders'),
    title: r.merchant_order_number,
    subtitle: r.asset_name ?? EMPTY,
    fields: [
      { k: str(t, 'cUser'), v: EMPTY }, // 计划 §6-P5：镜像无客户标识
      { k: str(t, 'cAmount'), v: r.amount === null ? EMPTY : `${usd(r.amount)}${r.currency && r.currency !== 'USD' ? ` ${r.currency}` : ''}` },
      { k: str(t, 'cStatus'), v: orderStatusText(t, r) },
      { k: str(t, 'cTime'), v: fmtDateTime(r.created_at, { tz: tz(ctx) }) },
    ],
    list: {
      title: logi,
      rows: r.shipment_status === null ? [] : [{ left: r.tracking_number ?? EMPTY, sub: '', right: '', rightSub: chipText(t, SHIP_STATUS, r.shipment_status), tone: r.shipment_status === 'transit' ? 'good' : 'warn' }],
    },
    actions,
  };
}

function orderCells(ctx: AdminCtx, r: PaymentOrderRow): readonly CellVals[] {
  return [
    cell(r.merchant_order_number, '', true),
    cell(r.asset_name ?? EMPTY),
    cell(EMPTY), // 计划 §6-P5
    cell(r.amount === null ? EMPTY : usd(r.amount)),
    orderChip(ctx.t, r),
    cell(fmtDateTime(r.created_at, { tz: tz(ctx) })),
  ];
}

function orderView(key: 'orders' | 'refunds'): ErasedView {
  return defineView<PaymentOrderRow>({
    key,
    // 06 C-17：两页签同一端点，以 tab 固定参数区分（spec §4.9 SpecOrders.Tabs = all / refund）
    source: { route: 'GET /api/admin/orders', tabParam: 'none', tabFacet: { orders: { tab: 'all' }, refunds: { tab: 'refund' } } },
    echo: '/admin/orders',
    cols: [{ label: 'cOrderId' }, { label: 'cAssetName' }, { label: 'cUser' }, { label: 'cAmount' }, { label: 'cStatus', facet: 'status' }, { label: 'cTime', sortKey: 'created_at' }],
    cells: orderCells,
    open: (c, r) => openDrawer(c, orderDrawer(c, r)),
    rowActions: () =>
      key === 'orders'
        ? [
            { label: 'aRefund', need: ORD_EDIT, danger: true, show: refundable, run: (c, r) => confirmRefund(c, r) },
            { label: 'aShip', need: ORD_EDIT, show: shippable, run: (c, r) => openForm(c, shipForm(c, r.id, r.merchant_order_number)) },
          ]
        : [{ label: 'aDetail', run: (c, r) => openDrawer(c, orderDrawer(c, r)) }],
    csvName: key === 'orders' ? 'orders' : 'orders-refunds',
  });
}

// ---- 物流跟踪 ----

async function cancelTrack(ctx: AdminCtx, row: LogisticsRow): Promise<void> {
  await withLock(ctx, `logi:cancel:${row.id}`, async () => {
    const alive = sessionGuard(ctx);
    try {
      await ctx.gp.api.call('POST /api/admin/order-logistics/{id}/cancel', { path: { id: row.id }, body: {} });
    } catch (e) {
      if (!alive()) return;
      return mapState(ctx, e, [1012], 'payErrTrackState');
    }
    if (!alive()) return;
    ctx.gp.ui.toast(str(ctx.t, 'toastLogisticsCancel'));
    if (ctx.host.state.tb.drawer) closeDrawer(ctx);
    await reloadCurrent(ctx);
  });
}

export function confirmCancelTrack(ctx: AdminCtx, row: LogisticsRow): void {
  const t = ctx.t;
  openConfirm(ctx, { title: str(t, 'cancelTrackTitle'), text: str(t, 'cancelTrackText'), label: `${row.merchant_order_number} · ${row.tracking_number ?? EMPTY}`, run: (c) => cancelTrack(c, row) });
}

const NODE_KEY: Readonly<Record<string, string>> = { paid: 'stPaid', ship_order: 'aShip', cancel_shipment: 'cancelTrackTitle' };

// 06 C-17 Tab2 时间线：本地事件节点 `{动作} · {carrier} {tracking_number} · {时间}`（时间由模板时间位承载）
function nodeLabel(t: Dict, n: TimelineNode): string {
  const act = str(t, NODE_KEY[n.kind] ?? '') || n.kind;
  const what = [n.carrier === null ? '' : carrierText(n.carrier, null), n.tracking_number ?? ''].filter((x) => x).join(' ');
  return what ? `${act} · ${what}` : act;
}

export function logisticsDrawer(ctx: AdminCtx, r: LogisticsRow): DrawerSpec {
  const t = ctx.t;
  const z = tz(ctx);
  const carrier = carrierText(r.carrier, r.carrier_name_other);
  return {
    kind: str(t, 'orders'),
    title: r.merchant_order_number,
    subtitle: `${carrier} · ${r.tracking_number ?? EMPTY}`,
    fields: [
      { k: str(t, 'cCarrier'), v: carrier },
      { k: str(t, 'cTracking'), v: r.tracking_number ?? EMPTY },
      { k: str(t, 'cStatus'), v: r.shipment_status === null ? EMPTY : chipText(t, SHIP_STATUS, r.shipment_status) },
      { k: str(t, 'cUpdated'), v: fmtDateTime(r.shipment_updated_at, { tz: z }) },
    ],
    timeline: r.timeline.map((n) => ({ label: nodeLabel(t, n), time: fmtDateTime(n.at, { tz: z }) })),
    actions:
      r.shipment_status === 'pending'
        ? [{ label: 'aShip', kind: 'primary', need: ORD_EDIT, run: (c) => openForm(c, shipForm(c, r.id, r.merchant_order_number)) }]
        : [{ label: 'aClose', kind: 'primary', run: (c) => closeDrawer(c) }],
  };
}

function logisticsView(): ErasedView {
  return defineView<LogisticsRow>({
    key: 'logistics',
    source: { route: 'GET /api/admin/order-logistics', tabParam: 'none' },
    echo: '/admin/order-logistics',
    cols: [
      { label: 'cOrderId' }, { label: 'cCarrier' }, { label: 'cTracking' }, { label: 'cStatus', facet: 'status', facetLabel: (t, v) => chipText(t, SHIP_STATUS, v) },
      { label: 'cUpdated', sortKey: 'updated_at' },
    ],
    cells: (c, r) => [
      cell(r.merchant_order_number, '', true),
      cell(carrierText(r.carrier, r.carrier_name_other)),
      cell(r.tracking_number ?? EMPTY, '', true),
      r.shipment_status === null ? cell(EMPTY) : chipOf(chipMap(c.t, SHIP_STATUS), r.shipment_status, 'order.shipment_status'),
      cell(fmtDateTime(r.shipment_updated_at, { tz: tz(c) })),
    ],
    open: (c, r) => openDrawer(c, logisticsDrawer(c, r)),
    rowActions: () => [{ label: 'aCancelTrack', need: ORD_EDIT, danger: true, show: (r) => r.shipment_status === 'transit', run: (c, r) => confirmCancelTrack(c, r) }],
    csvName: 'orders-logistics',
  });
}

// ---- 支付资产（只读；原型 asset 表单与新增 / 编辑 / 删除废弃，06 C-17 Tab3）----

const ASSET_TYPE: Readonly<Record<string, readonly [ChipKind, string]>> = { web: ['blue', 'Web'], app: ['purple', 'App'] };
const ASSET_STATUS: Readonly<Record<string, readonly [ChipKind, string]>> = { active: ['green', 'stActive'], pending: ['amber', 'stPending'] };

export function assetDrawer(ctx: AdminCtx, r: AssetRow): DrawerSpec {
  const t = ctx.t;
  return {
    kind: str(t, 'orders'),
    title: r.name,
    subtitle: r.id,
    fields: [
      { k: str(t, 'cAssetName'), v: r.name },
      { k: str(t, 'cAcctType'), v: ASSET_TYPE[r.account_type]?.[1] ?? r.account_type },
      { k: str(t, 'cDomain'), v: r.domain ?? EMPTY },
      { k: str(t, 'cStatus'), v: chipText(t, ASSET_STATUS, r.status) },
      { k: str(t, 'cCreated'), v: fmtDateTime(r.created_at, { tz: tz(ctx) }) },
    ],
    actions: [{ label: 'aClose', kind: 'primary', run: (c) => closeDrawer(c) }],
  };
}

function assetsView(): ErasedView {
  return defineView<AssetRow>({
    key: 'assets',
    source: { route: 'GET /api/admin/payment-assets', tabParam: 'none' },
    echo: '/admin/payment-assets',
    cols: [
      { label: 'cAssetName' }, { label: 'cAcctType', facet: 'account_type', facetLabel: (_t, v) => ASSET_TYPE[v]?.[1] ?? v }, { label: 'cDomain' },
      { label: 'cCreated', sortKey: 'created_at' }, { label: 'cStatus' },
    ],
    cells: (c, r) => [
      cell(r.name, r.id),
      chipOf(ASSET_TYPE, r.account_type, 'asset.account_type'),
      cell(r.domain ?? EMPTY, '', true),
      cell(fmtDateTime(r.created_at, { tz: tz(c) })),
      chipOf(chipMap(c.t, ASSET_STATUS), r.status, 'asset.status'),
    ],
    open: (c, r) => openDrawer(c, assetDrawer(c, r)),
    csvName: 'orders-assets',
  });
}

// TD-849：两页共用 I18N_PAYOUT（同一对象引用，table 域合并时去重）
const PACK = { i18n: I18N_PAYOUT, rewrites: REWRITES_PAYOUT } as const;

export const PAGES_PAYOUT: PageMap = {
  payout: { page: 'payout', ...PACK, tabLabels: (t) => strArr(t, 'tabPayout'), views: [payoutOrdersView(), accountsView(), quotaView()] },
  orders: { page: 'orders', ...PACK, tabLabels: (t) => strArr(t, 'tabOrders'), views: [orderView('orders'), orderView('refunds'), logisticsView(), assetsView()] },
};
